import { makeStyles, mergeClasses } from '@fluentui/react-components';
import { DonutLarge } from '@material-symbols-svg/react/rounded/w500';
import { useCallback, useEffect, useRef, useState } from 'react';

import { formatHM } from '../../common';
import { MONO_FONT, palette, tokens, useAppButtonStyles, useSurfaceStyles } from '../../theme';
import type { AllocationStats } from '../use-month-stats';

export type CapacityCardProps = {
    stats: AllocationStats;
    onBackfill: () => void;
};

// Auto-distribute is gated behind a rapid 10-click trigger. Clicks must land
// within QUICK_GAP_MS of each other; hitting UNLOCK_CLICKS plays 👍. Pausing
// at ABANDON_MIN_CLICKS..UNLOCK_CLICKS-1 plays 🖕. Below ABANDON_MIN_CLICKS
// the counter resets silently (so a stray double-click is ignored).
const UNLOCK_CLICKS = 10;
const ABANDON_MIN_CLICKS = 3;
const QUICK_GAP_MS = 400;
const FLASH_DURATION_MS = 1500;

type Flash = 'abandon' | 'celebrate' | null;

const FLASH_EMOJI: Record<Exclude<Flash, null>, string> = {
    abandon: '🖕',
    celebrate: '👍',
};

export const CapacityCard: React.FC<CapacityCardProps> = ({ stats, onBackfill }) => {
    const classes = useStyles();
    const surfaces = useSurfaceStyles();
    const buttons = useAppButtonStyles();

    const [clicks, setClicks] = useState(0);
    const [flash, setFlash] = useState<Flash>(null);
    const lastClickAtRef = useRef(0);
    const abandonTimerRef = useRef<number | null>(null);
    const flashTimerRef = useRef<number | null>(null);
    const unlocked = clicks >= UNLOCK_CLICKS;

    const clearTimers = useCallback(() => {
        if (abandonTimerRef.current !== null) {
            window.clearTimeout(abandonTimerRef.current);
            abandonTimerRef.current = null;
        }
        if (flashTimerRef.current !== null) {
            window.clearTimeout(flashTimerRef.current);
            flashTimerRef.current = null;
        }
    }, []);

    useEffect(() => clearTimers, [clearTimers]);

    const scheduleFlashHide = useCallback(() => {
        flashTimerRef.current = window.setTimeout(() => {
            flashTimerRef.current = null;
            setFlash(null);
        }, FLASH_DURATION_MS);
    }, []);

    const onTargetClick = useCallback(() => {
        if (unlocked) {
            return;
        }
        const now = Date.now();
        const gap = now - lastClickAtRef.current;
        lastClickAtRef.current = now;

        clearTimers();
        setFlash(null);

        const next = gap <= QUICK_GAP_MS ? clicks + 1 : 1;
        setClicks(next);

        if (next >= UNLOCK_CLICKS) {
            setFlash('celebrate');
            scheduleFlashHide();
            return;
        }

        abandonTimerRef.current = window.setTimeout(() => {
            abandonTimerRef.current = null;
            setClicks(0);
            if (next >= ABANDON_MIN_CLICKS) {
                setFlash('abandon');
                scheduleFlashHide();
            }
        }, QUICK_GAP_MS);
    }, [clicks, unlocked, clearTimers, scheduleFlashHide]);

    const bandLabel = `${Math.round(stats.targetMinRatio * 100)}-${Math.round(stats.targetMaxRatio * 100)}%`;

    return (
        <section className={mergeClasses(surfaces.card, classes.card)}>
            <div className={classes.cardTitle}>Capacity</div>

            <div className={classes.capacityRow}>
                <div className={classes.capacityItem}>
                    <span className={classes.capacityLabel}>Total capacity</span>
                    <span className={classes.capacityValue}>{formatHM(stats.capacity)} h</span>
                </div>
                <span className={classes.capacityDivider} aria-hidden />
                <div className={classes.capacityItem}>
                    <span className={classes.capacityLabel}>Target ({bandLabel})</span>
                    {/* Intentionally a plain span with no role/keyboard handler — this is the
                        hidden unlock trigger for Auto-distribute and must not be discoverable. */}
                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
                    <span
                        className={mergeClasses(classes.capacityValue, classes.capacityValueGood, classes.targetTrigger)}
                        onClick={onTargetClick}
                    >
                        {formatHM(stats.target)} h
                        {flash !== null && (
                            <span className={classes.flashEmoji} aria-hidden>
                                {FLASH_EMOJI[flash]}
                            </span>
                        )}
                    </span>
                </div>
            </div>

            {unlocked && (
                <>
                    <div className={classes.cardSeparator} aria-hidden />
                    <div className={classes.autoRow}>
                        <div className={classes.autoHint}>Distribute {bandLabel} of capacity</div>
                        <button type="button" className={buttons.primary} onClick={onBackfill}>
                            <DonutLarge className={classes.buttonIcon} aria-hidden />
                            <span>Auto-distribute</span>
                        </button>
                    </div>
                </>
            )}
        </section>
    );
};

const useStyles = makeStyles({
    card: {
        padding: '22px',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
    },
    cardTitle: {
        fontSize: '16px',
        fontWeight: tokens.fontWeightBold,
        color: tokens.colorNeutralForeground1,
    },
    cardSeparator: {
        height: '1px',
        backgroundColor: palette.cardBorder,
    },
    capacityRow: {
        display: 'flex',
        alignItems: 'center',
    },
    capacityItem: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        flex: 1,
        minWidth: 0,
    },
    capacityLabel: {
        fontSize: '12.5px',
        color: palette.dimText,
    },
    capacityValue: {
        fontFamily: MONO_FONT,
        fontSize: '20px',
        fontWeight: tokens.fontWeightBold,
        color: tokens.colorNeutralForeground1,
        lineHeight: 1.1,
    },
    capacityValueGood: {
        color: palette.accent,
    },
    targetTrigger: {
        position: 'relative',
        display: 'inline-block',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
    },
    flashEmoji: {
        position: 'absolute',
        left: '50%',
        top: '50%',
        fontSize: '56px',
        lineHeight: 1,
        pointerEvents: 'none',
        filter: 'drop-shadow(0 4px 10px rgba(0, 0, 0, 0.25))',
        animationName: {
            '0%': {
                opacity: 0,
                transform: 'translate(-50%, -50%) scale(0.2) rotate(-12deg)',
            },
            '15%': {
                opacity: 1,
                transform: 'translate(-50%, -50%) scale(1.4) rotate(6deg)',
            },
            '30%': {
                opacity: 1,
                transform: 'translate(-50%, -50%) scale(1.15) rotate(0deg)',
            },
            '100%': {
                opacity: 0,
                transform: 'translate(-50%, calc(-50% - 48px)) scale(1.05) rotate(0deg)',
            },
        },
        animationDuration: `${FLASH_DURATION_MS}ms`,
        animationTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
        animationFillMode: 'forwards',
    },
    capacityDivider: {
        width: '1px',
        alignSelf: 'stretch',
        backgroundColor: palette.cardBorder,
        margin: '0 16px',
    },
    autoRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
    },
    autoHint: {
        fontSize: '12.5px',
        color: palette.dimText,
        flexGrow: 1,
        minWidth: 0,
        lineHeight: 1.4,
    },
    buttonIcon: {
        width: '16px',
        height: '16px',
    },
});
